<!--
InputToggle Component - Toggle Switch Input Component

FEATURES:
=========
- Boolean toggle switch using PrimeVue ToggleSwitch
- Supports validation with visual indicators
- Handles disabled state
- Supports required field validation
- Emits both v-model and legacy input events
- Responsive layout with label
- Optional custom icons in toggle handle

PROPS:
======
- elemId: Unique identifier for the toggle element
- label: Label text displayed on the left
- validation: Function that returns boolean for validation state
- ngRequired: Boolean/string indicating if field is required
- disabled: Boolean/string indicating if field is disabled
- modelValue: Current toggle state (v-model) - boolean
- value: Legacy value prop for backward compatibility
- callback: Optional function to call when value changes
- invalid: Optional boolean to mark the toggle as invalid
- showIcons: Boolean to show check/times icons in the toggle handle
-->

<template>
  <div class="toggle-container">
    <div class="toggle-label" :class="{
      'validation-warning': !hasValidation,
      'validation-success': hasValidation
    }">
      <label :for="toggleInputId" class="field-label" :class="{ 'label-disabled': isDisabled }">
        <i v-if="isRequired" class="pi pi-exclamation-triangle validation-icon-warning" v-show="!hasValidation"></i>
        <i v-if="isRequired" class="pi pi-check validation-icon-success" v-show="hasValidation"></i>
        <span v-if="isRequired" class="required-indicator">*</span>{{ label }}:
      </label>
    </div>
    <div class="toggle-content" :id="'container_' + elemId">
      <ToggleSwitch
        :id="elemId"
        :inputId="toggleInputId"
        v-model="internalValue"
        :disabled="isDisabled"
        :invalid="isInvalid"
        @change="onChange"
      >
        <template v-if="showIcons" #handle="{ checked }">
          <i :class="['!text-xs pi', { 'pi-check': checked, 'pi-times': !checked }]" />
        </template>
      </ToggleSwitch>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import ToggleSwitch from 'primevue/toggleswitch';

const props = defineProps({
  // Optional callback function triggered when toggle changes
  callback: Function as import('vue').PropType<() => void>,

  // Unique identifier for the toggle element
  elemId: String,

  // Label text displayed to the left of the toggle
  label: String,

  // Validation function that returns boolean (true = valid, false = invalid)
  validation: {
    type: Function as import('vue').PropType<() => boolean>,
    default: () => true
  },

  // Indicates if the field is required (shows warning icon when invalid)
  ngRequired: {
    type: [String, Boolean],
    default: false
  },

  // Indicates if the field should be disabled
  disabled: {
    type: [String, Boolean],
    default: false
  },

  // Current toggle state (v-model support) - boolean
  modelValue: {
    type: Boolean,
    default: false
  },

  // Legacy value prop for backward compatibility
  value: Boolean,

  // Optional invalid state
  invalid: {
    type: Boolean,
    default: false
  },

  // Show check/times icons in toggle handle
  showIcons: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  'input': [value: boolean];
  'change': [event: Event];
}>();

// Reactive data
const internalValue = ref<boolean>(props.modelValue ?? props.value ?? false);

// Computed properties
const isRequired = computed((): boolean =>
  props.ngRequired === true || props.ngRequired === 'true'
);

const isDisabled = computed((): boolean =>
  props.disabled === true || props.disabled === 'true'
);

const hasValidation = computed((): boolean =>
  props.validation()
);

// PrimeVue puts `id` on the ToggleSwitch ROOT div, not on the <input role="switch">,
// so a <label> could never point at the control and the switch had no accessible
// name (axe: label). `inputId` targets the real input; keep `id` as-is so existing
// selectors on the wrapper keep working.
const toggleInputId = computed((): string | undefined =>
  props.elemId ? `${props.elemId}-input` : undefined
);

const isInvalid = computed((): boolean =>
  props.invalid || (isRequired.value && !hasValidation.value)
);

// Methods
const onChange = (event: Event): void => {
  if (!isDisabled.value) {
    emit('update:modelValue', internalValue.value);
    emit('input', internalValue.value); // Keep for backward compatibility
    emit('change', event);
    if (props.callback) {
      props.callback();
    }
  }
};

// Watchers
watch(() => props.modelValue, (newVal) => {
  internalValue.value = newVal ?? false;
});

watch(() => props.value, (newVal) => {
  if (newVal !== undefined) {
    internalValue.value = newVal ?? false;
  }
});
</script>

<style scoped>
/* Main Container */
.toggle-container {
  display: flex;
  gap: 20px;
  margin: 15px 0;
  align-items: center;
  margin-bottom: 1rem;
}

/* Label Section */
.toggle-label {
  flex: 0 0 20%;
  min-width: 120px;
  text-align: right;
}

.field-label {
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin: 0;
}

.label-disabled {
  opacity: 0.5;
}

.required-indicator {
  color: #f39c12;
  margin-right: 2px;
}

/* Validation Icons */
.validation-icon-warning {
  color: #f39c12;
  font-size: 0.875rem;
}

.validation-icon-success {
  color: #00a65a;
  font-size: 0.875rem;
}

/* Content Section */
.toggle-content {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
}

/* Responsive: Stack on tablet and below */
@media (max-width: 768px) {
  .toggle-container {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }

  .toggle-label {
    flex: 1 1 100%;
    text-align: left;
  }

  .field-label {
    font-size: 0.875rem;
  }

  .toggle-content {
    width: 100%;
  }
}
</style>

